import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Map from "@/components/Map";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { FileText, MapPin, List, Clock, Navigation, TagIcon, Wallet, Loader2, Gem } from "lucide-react";
import { guide } from "@/api/apiService";
import { useAppDispatch } from "@/lib/hooks";
import { Guide, Attractions, Transports, Marker, setCurrentGuide } from "@/features/guides/guidesSlice";
import { getCountries } from "@/api/apiService";
import { flagUrl, getPlaceMapLink, generatePDF, getGoogleMapsUrl } from "@/helpers.ts";

const GuideDetails = () => {
  const { id } = useParams();
  const [showMap, setShowMap] = useState(true);
  const [guideData, setGuideData] = useState<Guide>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [countryCode, setCountryCode] = useState<string>("");
  const dispatch = useAppDispatch();

  useEffect(() => {
    const fetchGuideDetails = async () => {
      setLoading(true);
      try {
        if (!id) throw new Error("Guide ID is required");

        const response: Guide = await guide(id);
        if (!response || !response.id) throw new Error("Guide not found");

        try {
          const countries = await getCountries();
          if (countries) {
            const country = countries.find((c: any) =>
                c.name.toLowerCase() === response.country.toLowerCase()
            );
            if (country) {
              setCountryCode(country.isoCode);
            }
          }
        } catch (error) {
          console.error('Failed to fetch countries for country code:', error);
        }

        setGuideData(response);
        dispatch(setCurrentGuide(response));

      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load guide");
      } finally {
        setLoading(false);
      }
    };

    fetchGuideDetails().then();
  }, [id, dispatch]);

  const renderArrayToString = (array: Attractions[] | Transports[]) => {
    if (Array.isArray(array)) {
      return array.map( item => item.name ).join(", ");
    }
    return "";
  }

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="h-12 w-12 animate-spin mx-auto text-primary" />
            <p className="mt-4 text-lg text-muted-foreground">Loading guide...</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (error || !guideData) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center max-w-md mx-auto p-6">
            <h2 className="text-2xl font-bold mb-4">Guide Not Found</h2>
            <p className="text-muted-foreground mb-6">{error || "Unable to load guide details"}</p>
            <Button onClick={() => window.history.back()}>Go Back</Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      <main className="flex-1 container mx-auto px-4 py-4 mt-16">
        <div className="flex flex-col lg:flex-row gap-4">
          <Card className="lg:w-1/3 p-4 lg:p-6 space-y-4 order-1 lg:order-1">
            <div className="flex items-center gap-4 p-4 bg-primary/5 rounded-lg relative overflow-hidden">
              <div className="relative z-20 flex items-center gap-4 p-4">
                <img src={flagUrl(guideData.country)} className="text-5xl" alt={`Flag of ${guideData.country}`}/>
                <div>
                  <h1 className="text-2xl font-bold text-white">{guideData.city}</h1>
                  <p className="text-white/80">{guideData.country}</p>
                </div>
              </div>
              <div className="absolute inset-0 bg-black/60 z-10"></div>
              <div
                  className="absolute inset-0 bg-cover bg-center z-0"
                  style={{
                    backgroundImage: `url('${guideData.image_url || '/public/placeholder.svg'}')`
                  }}
              ></div>
            </div>

            <div className="grid grid-cols-2 gap-3 mt-4">
              <div className="p-3 bg-accent/10 rounded-lg">
                <div className="flex items-center gap-2 mb-1">
                  <Clock className="h-4 w-4 text-primary"/>
                  <h2 className="font-semibold text-sm">Time of Visit</h2>
                </div>
                <p className="text-sm">{guideData.day_period}</p>
              </div>

              <div className="p-3 bg-accent/10 rounded-lg">
                <div className="flex items-center gap-2 mb-1">
                  <Clock className="h-4 w-4 text-primary" />
                  <h2 className="font-semibold text-sm">Duration</h2>
                </div>
                <p className="text-sm">{guideData.duration} hours</p>
              </div>

              <div className="p-3 bg-accent/10 rounded-lg col-span-2">
                <div className="flex items-center gap-2 mb-1">
                  <TagIcon className="h-4 w-4 text-primary" />
                  <h2 className="font-semibold text-sm">Attractions</h2>
                </div>
                <p className="text-sm">{ renderArrayToString(guideData.attractions) }</p>
              </div>

              <div className="p-3 bg-accent/10 rounded-lg">
                <div className="flex items-center gap-2 mb-1">
                  <Navigation className="h-4 w-4 text-primary" />
                  <h2 className="font-semibold text-sm">Transport</h2>
                </div>
                <p className="text-sm">{ renderArrayToString(guideData.transports) }</p>
              </div>

              <div className="p-3 bg-accent/10 rounded-lg">
                <div className="flex items-center gap-2 mb-1">
                  <Wallet className="h-4 w-4 text-primary" />
                  <h2 className="font-semibold text-sm">Free or paid</h2>
                </div>
                <p className="text-sm">{guideData.payment_preference}</p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col gap-3 pt-4">
              {/* Primary Action - Google Maps (most important) */}
              {guideData.markers && guideData.markers.length > 0 && (
                <Button
                  variant="outline"
                  className="w-full flex items-center justify-center gap-3 py-3 px-4 bg-white hover:bg-gray-50 border-gray-200"
                  onClick={() => window.open(getGoogleMapsUrl(guideData.markers, `${guideData.city},${guideData.country}`), '_blank')}
                >
                  <div className="flex-shrink-0">
                    <img src="/images/Google Maps.png" alt="Google Maps" className="h-6 w-6" />
                  </div>
                  <span className="text-gray-900 font-medium">Open Route in Google Maps</span>
                </Button>
              )}

              <div className="grid grid-cols-2 gap-2">
                <Button
                  variant="outline"
                  className="flex items-center justify-center gap-2 py-2.5 border-primary"
                  onClick={() => setShowMap(!showMap)}
                >
                  {showMap ? (
                    <>
                      <List className="h-4 w-4" />
                      <span className="hidden sm:inline">List</span>
                    </>
                  ) : (
                    <>
                      <MapPin className="h-4 w-4" />
                      <span className="hidden sm:inline">Map</span>
                    </>
                  )}
                </Button>
                <Button
                  variant="outline"
                  className="flex items-center justify-center gap-2 py-2.5"
                  onClick={() => generatePDF(guideData.markers, `${guideData.city},${guideData.country}`)}
                >
                  <FileText className="h-4 w-4" />
                  <span className="hidden sm:inline">PDF</span>
                </Button>
              </div>
            </div>
          </Card>

          <div className="lg:w-2/3 min-h-[90vh] order-2 lg:order-2">
            {showMap ? (
              <Card className="w-full h-[60vh] lg:h-full overflow-hidden">
                <Map
                  className="w-full h-full"
                  selectedCountry={guideData.country}
                  countryCode={countryCode}
                  markers={guideData.markers}
                />
              </Card>
            ) : (
              <Card className="w-full p-4 lg:p-6">
                <h2 className="text-2xl font-bold mb-6 gradient-text">Detailed Itinerary</h2>
                <div className="space-y-6">
                  {guideData.markers && guideData.markers.length > 0 ? (
                    guideData.markers.map((item: Marker, index: number) => (
                        <div key={index} className="relative pl-6 pb-6 border-l-2 border-primary/20">
                          <div className="absolute left-[-9px] top-0 w-4 h-4 rounded-full bg-primary"></div>
                          <h3 className="font-semibold text-lg text-primary flex justify-between">
                            {item.name || `Day ${index + 1}`}
                            {item.hidden_gem && (
                                <span className="ml-12" title="Hidden Gem">
                                  <Gem size={26} />
                                </span>
                            )}
                          </h3>
                          <p className="mt-2 text-muted-foreground">
                            {item.description}
                          </p>
                          <p className="mt-2 text-muted-foreground" dangerouslySetInnerHTML={{ __html: item.long_description.replace(/\n/g, "<br/>") }}>
                          </p>
                          {item.name && (
                              <a
                                  rel="noopener noreferrer"
                                  className="mt-2 block"
                                  href={ getPlaceMapLink(`${item.name},${guideData.city},${guideData.country}` )}
                                  target="_blank">
                                🗺️ Open in maps
                              </a>
                          )}
                        </div>
                    ))
                  ) : (
                      <h3 className="font-semibold text-lg text-primary">Something went wrong</h3>
                  )}
                </div>
              </Card>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default GuideDetails;
